/**
 * QA #15 — view-model PURO das ações do chrome global (Story 2.7).
 *
 * O nav era estático: oferecia "Entrar"/"Começar agora" a quem já tinha sessão, inclusive
 * dentro da dashboard, e o `side-menu` (gémeo mobile) repetia o mesmo "Entrar" fixo por ser
 * uma segunda lista escrita à mão. A decisão "que ações mostra o chrome" passa a viver aqui,
 * numa única fonte que ambos leem — a divergência desktop/mobile morre na raiz.
 *
 * Devolve DESCRITORES (dados), nunca elementos React: é o que mantém o módulo puro sob o
 * `environment: "node"` do vitest. Nav e SideMenu mapeiam o descritor para o seu próprio JSX
 * (desktop inline; mobile lista + CTA) — a aparência diverge de propósito, a decisão não.
 */

export const CHROME_ACTION_LOGIN = "login"
export const CHROME_ACTION_ACCOUNT = "account"
export const CHROME_ACTION_GET_STARTED = "get-started"

export type ChromeActionKey =
  | typeof CHROME_ACTION_LOGIN
  | typeof CHROME_ACTION_ACCOUNT
  | typeof CHROME_ACTION_GET_STARTED

/** `link` = texto do chrome; `primary` = fill blue-600 do `button-primary` (DESIGN.md). */
export type ChromeActionVariant = "link" | "primary"

export interface ChromeAction {
  key: ChromeActionKey
  label: string
  href: string
  variant: ChromeActionVariant
}

const ACCOUNT_HREF = "/account"
const PLANS_HREF = "/#planos"

const LABEL_LOGIN = "Entrar"
const LABEL_ACCOUNT = "Minha conta"
const LABEL_GET_STARTED = "Começar agora"

/**
 * Projeta a sessão nas ações do chrome. Recebe BOOLEANO, nunca o objeto `customer`: o Nav e o
 * SideMenu são client components, logo o customer seria serializado no payload RSC enviado a
 * CADA pedido (nome, e-mail, telefone) só para decidir que dois links mostrar. Um booleano diz
 * tudo o que o chrome precisa de saber e não expõe PII nenhuma.
 *
 * Autenticado ⇒ só o atalho de conta. Nem login (já tem sessão) nem "Começar agora" — quem já
 * é cliente não é convidado a converter outra vez pelo chrome.
 *
 * Story 3.8 — `isPostPurchase` cala a CONVERSÃO no ecrã de confirmação. Ali o visitante é
 * anónimo (comprou como convidado), logo o chrome oferecia-lhe "Começar agora": vender a
 * quem acabou de comprar, no ecrã onde ele espera a entrega. O "Entrar" fica — é útil a quem
 * já tinha conta — mas o convite a converter sai.
 */
export function toChromeActions(
  isAuthenticated: boolean,
  options?: { isPostPurchase?: boolean }
): ChromeAction[] {
  if (isAuthenticated) {
    return [
      {
        key: CHROME_ACTION_ACCOUNT,
        label: LABEL_ACCOUNT,
        href: ACCOUNT_HREF,
        variant: "link",
      },
    ]
  }

  const login: ChromeAction = {
    key: CHROME_ACTION_LOGIN,
    label: LABEL_LOGIN,
    href: ACCOUNT_HREF,
    variant: "link",
  }

  if (options?.isPostPurchase) {
    return [login]
  }

  return [
    login,
    {
      key: CHROME_ACTION_GET_STARTED,
      label: LABEL_GET_STARTED,
      href: PLANS_HREF,
      variant: "primary",
    },
  ]
}

/**
 * O ecrã de confirmação pós-compra. Vive aqui (e não no Nav) para o SideMenu ler a MESMA
 * regra — foi a divergência entre as duas listas que produziu o QA #15.
 */
export function isPostPurchasePath(pathname: string | null | undefined): boolean {
  return typeof pathname === "string" && /\/order\/[^/]+\/confirmed\/?$/.test(pathname)
}
